import { useEffect, useMemo, useState, useCallback, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faMoon, faSun } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { LoginContext } from "../context/loginContext";

const THEME_KEY = "theme";
const CLASS_DARK = "gleichklang-dark-mode";

function isDarkExtensionActive() {
    const html = document.documentElement;
    const body = document.body;

    // Dark Reader (most reliable signals)
    if (html.dataset.darkreaderScheme === "dark") return true;
    if (html.classList.contains("darkreader")) return true;

    // Firefox broswer Extension
    if(html.getAttribute('nighteye') == 'active') return true;

    // Other extensions using invert filter (best-effort)
    const h = getComputedStyle(html);
    const b = body ? getComputedStyle(body) : null;

    // Marker attribute used by our CSS to patch broken backgrounds when inversion is active.
    const style = document.getElementById('dark-reader-style');
    if (style) {
        const css = style.textContent || "";
        const res = (css.includes("-webkit-filter: invert") || css.includes("filter: invert") || css.includes("hue-rotate(180deg)"));
        if(res) {
            // Add attribute for restore some bg broken
            html.toggleAttribute("dark-reader-style", true);
        } else {
            html.removeAttribute("dark-reader-style");
        }
    }

    const filters = `${h.filter}${h.webkitFilter}${b?.filter ?? ""}${b?.webkitFilter ?? ""}`;
    if (filters.includes("invert") && !html.classList.contains(CLASS_DARK)) return true;

    // Aggressive inline body background injections (best-effort)
    const styleAttr = body?.getAttribute("style") ?? "";
    if (styleAttr.includes("background") && (styleAttr.includes("#") || styleAttr.includes("rgb")) && !styleAttr.includes("url(")) {
        return true;
    }

    // Other custom flags (yours)
    if (html.getAttribute("native-dark-active") != null) return true;

    return false;
}

function applyTheme(userDark, locked) {
    const html = document.documentElement;
    if (locked) {
        html.classList.remove(CLASS_DARK);
        localStorage.setItem(THEME_KEY, "light");
        return;
    }
    html.classList.toggle(CLASS_DARK, userDark);
    localStorage.setItem(THEME_KEY, userDark ? "dark" : "light");
}

export default function DarkModeCustomToggle() {
    const { t } = useTranslation();
    const [userDark, setUserDark] = useState(() => localStorage.getItem(THEME_KEY) === "dark");
    const [locked, setLocked] = useState(false);
    let { loginData: {accessToken} } = useContext(LoginContext)

    const refreshLock = useCallback(() => {
        setLocked(isDarkExtensionActive());
    }, []);

    // Detect extension at safe checkpoints (no DOM observers)
    useEffect(() => {
        refreshLock();

        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        
        const onMq = () => refreshLock();
        const onFocus = () => refreshLock();
        const onVis = () => document.visibilityState === "visible" && refreshLock();

        mq.addEventListener?.("change", onMq);
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVis);

        return () => {
            mq.removeEventListener?.("change", onMq);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [refreshLock]);

    // Cross-tab sync + apply theme
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === THEME_KEY) setUserDark(e.newValue === "dark");
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    useEffect(() => {
        applyTheme(userDark, locked);
    }, [userDark, locked]);

    const visualActive = useMemo(() => userDark || locked, [userDark, locked]);

    return (
        <div
            className={`darkmode-toggle ${locked ? "darkmode-toggle-locked" : ""}`}
            role="button"
            tabIndex={locked ? -1 : 0}
            aria-pressed={visualActive}
            onClick={() => !locked && setUserDark(v => !v)}
            onKeyDown={e => (e.key === "Enter" || e.key === " ") && !locked && setUserDark(v => !v)}
            title={locked ? t("member.header.toggleIsLocked") : t("member.header.switch_dark_mode")}
        >
            <div className={`toggle-slider ${visualActive ? "active" : ""}`}>
                <FontAwesomeIcon icon={locked ? faLock : visualActive ? faMoon : faSun} />
            </div>
            {locked && <div className={`tipDarkForce locked ${!accessToken && 'tipDarkForceNonMember'}`}> {t("member.header.toggleIsLocked")}</div>}
        </div>
    );
}
